<!-- Left Side -->
<div class="flex flex-column lg:h-screen h-auto overflow-hidden bg-surface-0">
    <div class="flex-none border-bottom-1 border-300 bg-surface-0 z-2">
        <div
            class="px-3 py-2 flex align-items-center justify-content-between w-full"
            [ngClass]="isCurrentTenantClient() ? 'surface-0' : 'bg-yellow-100'">
            <div class="flex align-items-center gap-2">
              @if (showCloseIcon()) {
                <div
                    (click)="closeSidebar()"
                    (keydown)="onCloseKeydown($event)"
                    class="flex align-items-center justify-content-center w-2rem h-2rem surface-100 cursor-pointer relative z-5"
                    tabindex="0"
                    aria-label="close-profile-panel">
                    <i class="text-600 fa fa-times text-xl"></i>
                </div>
              }
              @if (isInboxContext() && !showSkeleton() && client() && client().id) {
                <button type="button"
                  (click)="openFullProfile.emit()"
                  class="flex align-items-center gap-2 px-3 py-1 border-1 border-primary border-round text-primary bg-white cursor-pointer text-xs font-medium transition-all transition-duration-150 hover:bg-primary hover:text-white"
                  aria-label="view-full-profile-button">
                  <i class="fa fa-external-link-alt text-xs"></i>
                  <span>{{ 'CLIENT_PROFILE.VIEW_FULL_PROFILE' | translate }}</span>
                </button>
              }
            </div>

            @if (!showSkeleton()) {
              <div class="flex align-items-center justify-content-end" [ngClass]="{'pointer-events-none opacity-60': client() && client().is_deleted}">
                @if (selectedClientInfo() && selectedClientInfo().name) {
                  <p-button
                      icon="fa fa-angle-down text-sm"
                      iconPos="right"
                      [label]="selectedClientInfo().name + ' - ' + selectedClientInfo().value"
                      styleClass="text-xs p-button-outlined py-2 text-600 border-600 surface-0 mr-2"
                      (click)="profileAction.toggle($event)"
                      (keydown)="onDropdownKeydown($event, profileAction)"
                      [attr.aria-label]="'client-info-dropdown'"
                      [attr.data-testid]="'client-info-dropdown'"
                  ></p-button>
                }
                <p-popover
                    #profileAction
                    styleClass="surface-0 shadow-3 border-1 border-300 border-round text-sm w-15rem"
                    [attr.aria-label]="'client-info-popover'"
                    [attr.data-testid]="'client-info-popover'">
                    <ng-template #content>
                        <div class="p-1">
                            @for (clientInfo of clientInfos | async; track clientInfo; let i = $index) {
                              <div
                                  class="cursor-pointer hover:surface-100 px-2 py-2 border-round transition-colors transition-duration-150 mb-1 last:mb-0"
                                  (click)="copyToClipboard(clientInfo.value)"
                                  (keydown)="onCopyKeydown($event, clientInfo.value)"
                                  [attr.aria-label]="'client-info-item-' + clientInfo.name.toLowerCase().replace(' ', '-')"
                                  [attr.data-testid]="'client-info-item-' + i"
                                  tabindex="0"
                              >
                                <div class="flex align-items-center justify-content-between">
                                    <div class="flex flex-column overflow-hidden mr-2">
                                        <span
                                            class="text-xs text-500 mb-1 uppercase"
                                            [attr.data-testid]="'client-info-name-' + i"
                                            >{{clientInfo.name}}</span
                                        >
                                        <span
                                            class="text-sm font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                                            [attr.data-testid]="'client-info-value-' + i"
                                            >{{clientInfo.value}}</span
                                        >
                                    </div>
                                    <div class="flex align-items-center justify-content-center w-2rem h-1rem border-round surface-100 text-600 flex-shrink-0">
                                        <i
                                            class="fa fa-copy text-sm"
                                            [attr.aria-label]="'copy-' + clientInfo.name.toLowerCase().replace(' ', '-')"
                                            [attr.data-testid]="'copy-icon-' + i"
                                        ></i>
                                    </div>
                                </div>
                              </div>
                            }
                        </div>
                    </ng-template>
                </p-popover>
                @if (client() && client().preferred_language) {
                  <div
                      class="flex align-items-center text-xs surface-200 py-1 px-3 border-round"
                      pTooltip="Preferred Language"
                      tooltipStyleClass="text-xs"
                  >
                      <img width="15" [src]="getClientPreferredLanguageImage(client().preferred_language)" alt="{{ 'common.LANGUAGE.' + client().preferred_language.toUpperCase() | translate }} flag" />
                      <div class="ml-1">
                          {{ 'common.LANGUAGE.' + client().preferred_language.toUpperCase() | translate}}
                      </div>
                  </div>
                }
              </div>
            }
            @if (showSkeleton()) {
              <div class="flex align-items-center justify-content-end">
                <p-skeleton width="8rem" height="1.3rem" styleClass="mr-3"></p-skeleton>
                <p-skeleton width="5rem" height="1.3rem"></p-skeleton>
              </div>
            }
        </div>

        <!-- Name and Image -->
        <div class="px-3 flex align-items-center mt-1 mb-3" [ngClass]="{'pointer-events-none opacity-60': client() && client().is_deleted}">
            @if (!showSkeleton()) {
              <div class="relative">
                @if (client() && client().image) {
                  <p-avatar
                      [image]="client().image"
                      shape="circle"
                      styleClass="w-5rem h-5rem"
                  ></p-avatar>
                }
                @if (client() && !client().image) {
                  <p-avatar
                      [label]="client().full_name | titlecase | shortName"
                      shape="circle"
                      styleClass="w-5rem h-5rem text-xl bg-surface-200 text-700"
                  ></p-avatar>
                }
                @if (client() && hasAnyRelation(client()) && client().parent_client_id > 0) {
                  <div
                      (mouseenter)="relation_detail.toggle($event)"
                      class="bg-purple-400 text-white border-round-xl px-2 py-1 font-bold absolute right-0 top-0 text-xs -mr-4 -mt-2"
                      aria-label="client-relation-badge"
                  >
                      {{client().relation}}
                  </div>
                }
                <p-popover #relation_detail styleClass="overflow-hidden mt-0" aria-label="relation-details-popover">
                    <ng-template #content>
                        @for (relation of getRelationDetails(); track relation) {
                          <div
                              class="flex align-items-center text-sm -m-2"
                              aria-label="relation-detail-item"
                          >
                            <p-avatar
                                [image]="relation.relationImage"
                                shape="circle"
                                aria-label="relation-avatar"
                            ></p-avatar>
                            <div class="ml-1">
                                <div class="font-bold mb-1" aria-label="relation-name">{{relation.relationName}}</div>
                                <div class="text-xxs" aria-label="relation-type">
                                    Relation: {{relation.relationType}}
                                </div>
                            </div>
                          </div>
                        }
                    </ng-template>
                </p-popover>
              </div>
            }
            @if (showSkeleton()) {
              <p-skeleton width="4rem" height="4rem" shape="circle"></p-skeleton>
            }
            <div [ngClass]="hasAnyRelation(client()) && client().parent_client_id > 0 ? 'ml-4' : 'ml-3'">
                @if (!showSkeleton() && client()) {
                  <div class="font-bold text-lg flex align-items-center mb-2 text-900">
                      <span>{{client().full_name}}</span>
                      <i [class]="'fa fa-crown ' + crownType(client().client_since).color + ' ml-2 text-sm'"></i>
                      @if (client().is_digital_keyfob) {
                        <i
                            [class]="'fa fa-key ml-2 text-green-500'"
                            pTooltip="{{ 'CLIENT_PROFILE.DIGITAL_ACCESS_PASS' | translate }}"
                            tooltipStyleClass="text-xs"
                        ></i>
                      }
                  </div>
                }
                @if (showSkeleton()) {
                  <p-skeleton width="5rem" height="1.1rem" styleClass="mb-2"></p-skeleton>
                }

                <!-- Badge -->
                @if (!showSkeleton() && client()) {
                  <div class="flex flex-wrap gap-2 align-items-center">
                      @if (client().track_mode && client().track_mode.toLowerCase() === 'clientwebsignup') {
                        <span
                            class="bg-green-100 px-2 py-1 border-round text-green-700 text-xxs font-bold uppercase"
                        >
                            {{ 'CLIENT_PROFILE.WEBSITE_LEAD' | translate }}
                        </span>
                      }
                      <div [innerHTML]="clientStatus(client())" class=""></div>
                      @if (client().stage_master && currentStageStatus()) {
                        <span
                            class="bg-bluegray-50 text-bluegray-500 px-2 py-1 border-round border-1 border-bluegray-200 text-xxs uppercase"
                        >
                            {{ currentStageStatus() }}
                        </span>
                      }
                  </div>
                }
                @if (showSkeleton()) {
                  <div class="flex align-items-center">
                    <p-skeleton height="1.25rem" styleClass="w-4rem"></p-skeleton>
                    <p-skeleton height="1.25rem" styleClass="w-4rem ml-1"></p-skeleton>
                    <p-skeleton height="1.25rem" styleClass="w-4rem ml-1"></p-skeleton>
                  </div>
                }
            </div>
        </div>
        <!-- Badge -->

        <!-- Info Icons -->
        @if (!showSkeleton() && client()) {
          <div class="px-3 flex gap-2 text-700 mb-3 relative z-1" [ngClass]="{'pointer-events-none opacity-60': client() && client().is_deleted}">
            <div
                class="flex-1"
                pTooltip="{{ 'CLIENT_PROFILE.MAKE_CALL' | translate }}"
                tooltipStyleClass="text-xs"
                tooltipPosition="bottom"
            >
                <div
                    class="h-3rem border-1 border-200 border-round flex align-items-center justify-content-center cursor-pointer text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                >
                    <a class="text-gray-600 w-full h-full flex align-items-center justify-content-center" href="tel:{{ client().mobile_country_code + client().mobile_number }}">
                        <i class="fa fa-phone text-lg"></i>
                    </a>
                </div>
            </div>
            <div
                class="flex-1"
                pTooltip="{{ 'CLIENT_PROFILE.SEND-SMS' | translate }}"
                tooltipStyleClass="text-xs"
                tooltipPosition="bottom"
            >
                <div
                    class="h-3rem border-1 border-200 border-round flex align-items-center justify-content-center cursor-pointer text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                    (click)="toggleCreateSmsDialog()"
                    (keydown)="onSmsKeydown($event)"
                    tabindex="0"
                >
                    <i class="fa fa-comment text-lg"></i>
                </div>
            </div>
            <div
                class="flex-1"
                pTooltip="{{ 'CLIENT_PROFILE.SEND-WHATS-APP' | translate }}"
                tooltipStyleClass="text-xs"
                tooltipPosition="bottom"
            >
                <div
                    (click)="whatsappDropdown.toggle($event)"
                    (keydown)="onWhatsappKeydown($event, whatsappDropdown)"
                    class="h-3rem border-1 border-200 border-round flex align-items-center justify-content-center cursor-pointer text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                    tabindex="0"
                >
                    <i class="fab fa-whatsapp text-lg"></i>
                </div>
                <p-popover #whatsappDropdown styleClass="overflow-hidden right-0 text-sm w-13rem -ml-5">
                    <ng-template #content>
                        <ul class="list-none -m-4 py-2 px-0 text-xs text-600">
                            <li class="cursor-pointer hover:surface-300 py-2 px-3">
                                <a
                                    class="text-600"
                                    href="https://api.whatsapp.com/send?phone={{ client().mobile_country_code+client().mobile_number }}&text=Hello"
                                    target="_blank"
                                    >{{ 'CLIENT_PROFILE.SEND_WHATSAPP_CHAT' | translate }}</a
                                >
                            </li>
                            <li class="cursor-pointer hover:surface-300 py-2 px-3" (click)="toggleSendWhatsAppDialog()" (keydown)="onWhatsappTemplateKeydown($event)" tabindex="0">
                                {{ 'CLIENT_PROFILE.SEND_WHATSAPP_TEMPLATE' | translate }}
                            </li>
                        </ul>
                    </ng-template>
                </p-popover>
            </div>
            <div
                class="flex-1"
                pTooltip="{{ 'CLIENT_PROFILE.SEND-EMAIL' | translate }}"
                tooltipStyleClass="text-xs"
                tooltipPosition="bottom"
            >
                <div
                    class="h-3rem border-1 border-200 border-round flex align-items-center justify-content-center cursor-pointer text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                    (click)="toggleSendEmailDialog()"
                    (keydown)="onEmailKeydown($event)"
                    tabindex="0"
                >
                    <i class="fa fa-envelope text-lg"></i>
                </div>
            </div>
            <div
                class="flex-1"
                pTooltip="{{ 'CLIENT_PROFILE.BOOK_TOOLTIP' | translate }}"
                tooltipStyleClass="text-xs"
                tooltipPosition="bottom"
            >
                <div
                    class="h-3rem border-1 border-200 border-round flex align-items-center justify-content-center cursor-pointer text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                    (click)="showBookAppointmentPopup.set(true)"
                    (keydown)="onBookAppointmentKeydown($event)"
                    tabindex="0"
                >
                    <i class="fa fa-plus text-lg"></i>
                </div>
            </div>
            <div class="flex-1 text-center relative">
                <fitness-force-prospect-list-actions
                    [columnData]="clientDetails()"
                    [tableAction]="false"
                    [showDeleteDigitalPass]="showDeleteDigitalPass()"
                    (changeEvent)="changeInAction($event)"
                    class="block h-3rem w-full"
                >
                </fitness-force-prospect-list-actions>
            </div>
          </div>
        }

        <!-- Info Icons Skeleton -->
        @if (showSkeleton()) {
          <div class="px-3 flex mt-2 gap-2">
            <div class="flex-1"><p-skeleton styleClass="h-3rem w-full"></p-skeleton></div>
            <div class="flex-1"><p-skeleton styleClass="h-3rem w-full"></p-skeleton></div>
            <div class="flex-1"><p-skeleton styleClass="h-3rem w-full"></p-skeleton></div>
            <div class="flex-1"><p-skeleton styleClass="h-3rem w-full"></p-skeleton></div>
            <div class="flex-1"><p-skeleton styleClass="h-3rem w-full"></p-skeleton></div>
            <div class="flex-1"><p-skeleton styleClass="h-3rem w-full"></p-skeleton></div>
          </div>
        }
    </div>

    <!-- Client Details Wrapper -->
    <div class="flex-grow-1 overflow-y-auto hidden md:block" [ngClass]="{'pointer-events-none opacity-60': client() && client().is_deleted}">
        <fitness-force-client-profile-details-tab
            [client]="client()"
            [showSkeleton$]="showSkeleton$()"
            [tenantId]="tenantId()"
        ></fitness-force-client-profile-details-tab>
    </div>
    <!-- Client Details Wrapper -->
</div>

    <hr
    [ngClass]="isCurrentTenantClient() ? 'border-300' : 'border-white'"
    class="md:hidden border-top-1 border-bottom-none border-left-none border-right-none my-0"
/>
<!-- Left Side -->

<!-- Send SMS Modal -->
@if (toggleCreateSmsDialog$ | async) {
  <send-bulk-sms
      [visible]="toggleCreateSmsDialog$.value"
      [tenantId]="currentTenantId()"
      [SelectedClient]="SelectedClient"
      (hide)="closeCreateSmsDialog($event)"
  >
  </send-bulk-sms>
}
<!-- Send SMS Modal -->

<!-- Send Email Modal -->
@if (toggleSendEmailDialog$ | async) {
  <send-bulk-email
      [visible]="toggleSendEmailDialog$.value"
      [tenantId]="currentTenantId()"
      [SelectedClient]="SelectedClient"
      (hide)="closeSendEmailDialog($event)"
  >
  </send-bulk-email>
}
<!-- Send Email Modal -->

<!-- Send WhatsApp Modal -->
@if (toggleSendWhatsAppDialog$ | async) {
  <send-whatsapp
      [visible]="toggleSendWhatsAppDialog$.value"
      [tenantId]="currentTenantId()"
      [SelectedClient]="SelectedClient"
      (hide)="closeSendWhatsAppDialog($event)"
  >
  </send-whatsapp>
}
<!-- Send WhatsApp Modal -->

@if (showBookAppointmentPopup()) {
  <book-appointment
      [visible]="showBookAppointmentPopup()" (onHide)="CloseBookAppointment(false)" 
      [clientId]="client().id"
      [tenantId]="currentTenantId()"
      (CloseBookAppointment)="CloseBookAppointment($event)"
  >
  </book-appointment>
}

